import "./Legal.css";

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <h1>Privacy Policy</h1>
      <p><b>Effective Date:</b> January 1, 2026</p>

      <p>
        This Privacy Policy explains how EPS Viewer (&quot;we&quot;, &quot;us&quot;,
        or &quot;our&quot;), a service operated by{" "}
        <a href="https://magicapps.co.in/" target="_blank" rel="noopener noreferrer">MagicApps</a>,
        collects, uses, and protects information when you use{" "}
        <b>epsviewer.in</b> (the &quot;Website&quot;). By using the Website, you
        agree to this Policy. If you do not agree, please do not use the Website.
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <b>Files you upload:</b> When you use the viewer, your EPS file is sent
          to our servers to be rendered and converted. We do not require an account
          and we do not ask for personal information to use the tool.
        </li>
        <li>
          <b>Contact form:</b> If you write to us through the contact form, we receive the
          name, email address, subject and message you submit. Delivery is handled on our
          behalf by{" "}
          <a href="https://web3forms.com/privacy" target="_blank" rel="noopener noreferrer">Web3Forms</a>,
          which forwards the message to our support inbox. We use these details only to
          reply to you and to keep a record of the correspondence.
        </li>
        <li>
          <b>Usage data:</b> We collect aggregated, anonymized information about how
          the Website is used (for example, pages viewed and features used) through
          analytics tools.
        </li>
        <li>
          <b>Log &amp; device data:</b> Like most websites, our servers and
          third-party providers may automatically record standard information such
          as IP address, browser type, device type, and access times.
        </li>
        <li>
          <b>Cookies:</b> We and our partners use cookies and similar technologies
          as described in our <a href="/cookie-policy">Cookie Policy</a>.
        </li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To provide the core service — viewing and converting your EPS files.</li>
        <li>To operate, maintain, secure, and improve the Website.</li>
        <li>To understand usage through aggregated analytics.</li>
        <li>To display advertising that helps keep the service free.</li>
      </ul>

      <h2>3. File Handling &amp; Retention</h2>
      <ul>
        <li>Uploaded files are processed on a temporary basis to generate your preview and downloads.</li>
        <li>Files and their generated outputs are automatically removed from our servers after processing; we do not keep them permanently.</li>
        <li>Please do not upload confidential or sensitive information.</li>
      </ul>

      <h2>4. Cookies</h2>
      <p>
        We use strictly necessary, functional, analytics, and advertising cookies.
        For full details and how to control them, see our{" "}
        <a href="/cookie-policy">Cookie Policy</a>.
      </p>

      <h2>5. Advertising &amp; Google AdSense</h2>
      <p>
        We use third-party advertising companies, including <b>Google AdSense</b>,
        to display ads on this Website. These partners may use cookies and similar
        technologies to collect non-personally-identifiable information about your
        visits to this and other websites in order to show ads relevant to you.
      </p>
      <ul>
        <li>
          Google, as a third-party vendor, uses cookies to serve ads on our site.
          Google&apos;s use of advertising cookies enables it and its partners to
          serve ads based on your visits to this and/or other sites on the Internet.
        </li>
        <li>
          You can opt out of personalized advertising through{" "}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>,
          or opt out of third-party vendor cookies at{" "}
          <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a>.
        </li>
        <li>
          See also{" "}
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">How Google uses information from sites that use its services</a>.
        </li>
      </ul>

      <h2>6. Analytics</h2>
      <p>
        We use analytics tools such as Google Analytics and Firebase to understand
        how visitors use the Website — for example, which pages are viewed and which
        features are used. This information is aggregated and is not used to
        personally identify you.
      </p>

      <h2>7. How We Share Information</h2>
      <p>
        We do not sell your personal information. We share information only with the
        service providers that help us run the Website (such as hosting, analytics,
        and advertising partners), and where required by law.
      </p>

      <h2>8. Data Security</h2>
      <p>
        Files are transferred over encrypted (HTTPS) connections, and we apply
        reasonable technical measures to protect data. However, no method of
        transmission or storage is 100% secure, and we cannot guarantee absolute
        security.
      </p>

      <h2>9. Your Rights</h2>
      <p>
        Depending on your location, you may have rights under laws such as the GDPR
        (EEA/UK) and CCPA (California), including the right to access, correct, or
        delete your personal data, to object to or restrict processing, and to opt
        out of personalized advertising. Because we do not require accounts and do
        not retain uploaded files, we typically hold little or no personal data. To
        make a request, contact us at{" "}
        <a href="mailto:support@magicapps.co.in?subject=EPS%20Viewer%20%26%20Converter">support@magicapps.co.in</a>.
      </p>

      <h2>10. International Users</h2>
      <p>
        The Website is operated from India and may be accessed worldwide. By using
        it, you understand your information may be processed in countries other than
        your own.
      </p>

      <h2>11. Children&apos;s Privacy</h2>
      <p>
        The Website is not directed to children under the age of 13 (or the
        equivalent minimum age in your jurisdiction), and we do not knowingly collect
        personal information from children. If you believe a child has provided us
        information, please contact us so we can remove it.
      </p>

      <h2>12. Third-Party Links</h2>
      <p>
        Our Website may link to third-party sites. We are not responsible for the
        content or privacy practices of those sites; please review their policies.
      </p>

      <h2>13. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be
        reflected by updating the &quot;Effective Date&quot; above. Continued use of
        the Website after changes constitutes acceptance of the updated Policy.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy, contact us at{" "}
        <a href="mailto:support@magicapps.co.in?subject=EPS%20Viewer%20%26%20Converter">support@magicapps.co.in</a>{" "}
        or via our <a href="/contact">Contact page</a>.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
